<script setup lang="ts">
// Modal de AVISO das promoções: a folha que sobe quando a roletinha ou o
// Check-in Diário não têm o que mostrar. Antes cada modal abria inteiro (topbar,
// logo, tagline) com uma frase solta no meio; aqui a folha é só arte de
// cabeçalho + o motivo +, quando o motivo é nível, o aviso de nível da loja
// (BaseLevelGateNotice) no lugar do que era uma lista fixa de requisitos de KYC.
//
// Figma: file MvX0d5Neu0A19lRCv3fsV4 → "Roleta" (285:6919) e "Check-in"
// (287:7903). Os dois frames são o MESMO desenho — só a imagem do cabeçalho
// muda —, então isto é um componente com a arte por prop.
//
// Ele não conhece store nem status: recebe arte, frase e os slugs do aviso de
// nível, então sobe num harness sem Pinia.
import { crmLevelTier } from '@/utils/levels';
import { crmLevelLabel } from '@/utils/apostatudo/crm-level-slug';

const props = withDefaults(
  defineProps<{
    // Arte do cabeçalho. Sangra pra CIMA da folha e tem a janela do Figma
    // (277,3 × 218,3), então a imagem precisa vir recortada nessa proporção — o
    // fade da base é feito aqui, em CSS.
    art: string;
    // O motivo, em uma frase. Varia por estado (sem roleta no ar, fora da
    // promoção, ...) — quem decide é quem abre a folha.
    message: string;
    // Segunda linha, menor. Só onde a copy existente tem uma (o 'unavailable' do
    // check-in promete a trilha pro próximo período).
    hint?: string;
    // Aviso de nível (o mesmo da loja) abaixo do divisor. SÓ no estado de
    // elegibilidade por nível (`gamifications.<slug>.is_eligible: false` no
    // /players/me) — nos outros motivos ele responderia outra pergunta. Os slugs
    // vêm do payload; quem abre a folha os lê da store de perfil.
    levelGate?: {
      subject: string;
      minLevel: string | null;
      currentLevel: string | null;
    } | null;
    // Cor das partículas do fundo. Cada feature tem a sua (azul na roletinha,
    // dourado no check-in), a MESMA que ela usa na folha cheia — o aviso é a
    // folha dela em outro estado, não uma tela de outro lugar.
    //
    // Com `levelGate`, quem manda é o ELO do degrau exigido (ver `gateTier`): a
    // folha inteira — título, partículas e o brilho do painel — fica na cor do
    // nível que falta, e não na da feature.
    accent?: string;
  }>(),
  { hint: '', levelGate: null, accent: '#5AA9FF' },
);

// 'navigate' = o CTA do aviso de nível levou o jogador pra trilha de níveis;
// quem abriu a folha fecha o modal.
defineEmits<{ (e: 'close'): void; (e: 'navigate'): void }>();

// Elo do degrau exigido — o mesmo helper do BaseLevelGateNotice, pra a folha e
// o aviso dentro dela concordarem na cor. `undefined` sem gate.
const gateTier = computed(() =>
  props.levelGate ? crmLevelTier(props.levelGate.minLevel) : undefined,
);

const gateLabel = computed(() => crmLevelLabel(props.levelGate?.minLevel));

// Partículas: cor do elo com gate, cor da feature sem.
const particlesColor = computed(() => gateTier.value?.accent ?? props.accent);

// Brilho do painel na cor do elo (mesmo desenho do aviso da loja). Só com gate:
// nos outros motivos o painel segue chapado, como no Figma.
const panelStyle = computed(() =>
  gateTier.value ? { '--nts-tint': gateTier.value.color } : undefined,
);

const titleStyle = computed(() =>
  gateTier.value ? { '--nts-accent': gateTier.value.accent } : undefined,
);
</script>

<template>
  <div class="nts">
    <!-- Cabeçalho = arte + X, os dois FORA do painel: a arte sangra 75px acima
         dele (e o painel é `overflow: clip`, pelas quinas arredondadas), e o X
         precisa ficar na frente da arte. Esta caixa também é a régua do X: a
         altura dela é a da arte, então o `top` em % dele escala com o desenho. -->
    <div class="nts__head">
      <img
        alt=""
        aria-hidden="true"
        class="nts__art"
        :src="props.art"
      />

      <button
        aria-label="Fechar"
        class="nts__close"
        type="button"
        @click="$emit('close')"
      >
        <!-- Inline e não asset: as duas features têm o próprio close.svg, e uma
             base component apontando pra pasta de uma delas amarraria as duas. -->
        <svg
          aria-hidden="true"
          fill="none"
          viewBox="0 0 30 30"
        >
          <rect
            fill="#fff"
            fill-opacity="0.05"
            height="30"
            rx="15"
            width="30"
          />
          <path
            d="M19.2784 12.1583L20 11.4399L18.5601 10L17.8417 10.7184L15 13.5601L12.1583 10.7184L11.4399 10L10 11.4399L10.7184 12.1583L13.5601 15L10.7216 17.8417L10 18.5601L11.4399 20L12.1583 19.2816L15 16.4399L17.8417 19.2816L18.5601 20L20 18.5601L19.2816 17.8417L16.4399 15L19.2816 12.1583H19.2784Z"
            fill="#fff"
          />
        </svg>
      </button>
    </div>

    <div
      class="nts__panel"
      :class="{ 'nts__panel--gate': props.levelGate }"
      :style="panelStyle"
    >
      <!-- Partículas no rés do chão do painel (o painel já clipa e é
           `position: relative`, que é tudo o que a camada precisa). O `travel`
           tem que passar da altura do painel, senão o ciclo reinicia à vista:
           295px é o painel alto (com os requisitos) no tamanho do Figma. -->
      <BaseParticlesField
        class="nts__particles"
        :color="particlesColor"
        travel="340px"
      />

      <!-- Com gate, o título é desenhado AQUI (e não a `message` crua) pra o
           trecho "nível Bronze 1" sair na cor do elo, centrado — a `message`
           que veio segue sendo o texto do mesmo título, e vale como fallback
           quando o core não mandou o nome do degrau. -->
      <p
        v-if="props.levelGate && gateLabel"
        class="nts__message nts__message--gate"
        :style="titleStyle"
      >
        Disponível a partir do <strong>nível {{ gateLabel }}</strong
        >.
      </p>

      <p
        v-else
        class="nts__message"
        :class="{ 'nts__message--gate': props.levelGate }"
      >
        {{ props.message }}
      </p>

      <p
        v-if="props.hint"
        class="nts__hint"
      >
        {{ props.hint }}
      </p>

      <!-- O divisor do Figma é a borda de cima deste bloco. -->
      <div
        v-if="props.levelGate"
        class="nts__reqs"
      >
        <BaseLevelGateNotice
          :current-level="props.levelGate.currentLevel"
          flat
          :min-level="props.levelGate.minLevel"
          :subject="props.levelGate.subject"
          @navigate="$emit('navigate')"
        />
      </div>

      <!-- Ações do motivo (ex.: "Tentar novamente" do estado de erro). -->
      <slot />
    </div>
  </div>
</template>

<style scoped lang="scss">
// Bottom-sheet colado no rodapé, como as outras folhas destas duas features: a
// largura vai até o teto de 393px (a do Figma) e o resto é respiro.
// Todas as medidas verticais do desenho estão em PORCENTAGEM da largura (em CSS,
// padding e margin verticais também resolvem pela largura), e é isso que faz a
// peça escalar inteira, sem breakpoint: `36.47%` é o mesmo 143px do Figma quando
// a folha tem 393px.
// CUIDADO: essas % têm que ficar em filhos da folha, nunca NA folha. O bloco
// contenedor dela é o overlay (a tela toda), então uma % aqui viraria fração da
// VIEWPORT — foi assim que a arte subiu pra fora do painel na primeira versão.
.nts {
  @apply relative z-10 select-none;

  // Celular: a folha cola nas laterais até 460px (entre 393 e 460 ela cresce
  // junto com a tela, sem respiro). Só a partir daí trava no teto de 393.
  width: 100vw;

  @media (min-width: 460px) {
    width: 393px;
  }
  margin: 0 auto;

  img {
    -webkit-user-drag: none;
  }
}

// Janela do Figma (277,3 × 218,3), centrada com o mesmo empurrão de 5,5px pra
// direita (1,407% da folha). A imagem já vem recortada nessa proporção; `cover` é
// o cinto de segurança pra uma arte que chegue com outra (as rodas de VIP são
// 734×562).
// Ela fica NO FLUXO (dentro do cabeçalho), e é o painel que sobe por baixo dela
// (ver `.nts__panel`): posicionada de forma absoluta, a sangria de cima teria que
// ser reservada por um padding na folha — a % proibida do comentário acima.
// O fade da base é a máscara: opaco até 62,95% da altura (137,4 de 218,3 no
// Figma) e transparente no fim — é assim que a arte "dissolve" no painel em vez
// de terminar numa aresta.
.nts__head {
  @apply relative z-[2];
}

.nts__art {
  position: relative;
  left: 1.407%;
  display: block;
  width: 70.56%;
  margin: 0 auto;
  aspect-ratio: 277.291 / 218.318;
  object-fit: cover;
  pointer-events: none;
  // Prefixado junto: `mask-image` sem prefixo só vale de iOS 15.4 pra cá, e sem
  // a máscara a arte terminaria numa aresta reta no meio do painel.
  -webkit-mask-image: linear-gradient(
    to bottom,
    #000 0,
    #000 62.95%,
    transparent 100%
  );
  mask-image: linear-gradient(to bottom, #000 0, #000 62.95%, transparent 100%);
}

// `margin-top` negativo = os 143px que a arte entra no painel (a sangria de 75px
// dela sobra acima); `padding-top` = os 151px do Figma até a frase, o que deixa
// ~8px de respiro entre a base da arte e o texto.
// z abaixo da arte pro fundo do painel não cobri-la.
.nts__panel {
  position: relative;
  z-index: 1;
  margin-top: -36.47%;
  padding: 38.42% 19px calc(env(safe-area-inset-bottom, 0px) + 33px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  // Colado na base: só os cantos de cima arredondam.
  border-bottom: 0;
  border-radius: 16px 16px 0 0;
  background: #000717;
  // `clip` e não `hidden`: `hidden` é scroll container, e aí o ajuste de foco do
  // clique desloca o painel (o mesmo bug das bordas da roletinha).
  overflow: clip;
}

// Com gate: o brilho do elo saindo de cima, sobre o mesmo fundo — o desenho do
// aviso da loja trazido pra folha. `--nts-tint` vem do painel (cor do elo). O
// `background` chapado acima fica como fallback pra navegador sem `color-mix`
// (que descarta a declaração inteira).
.nts__panel--gate {
  background:
    radial-gradient(
      120% 70% at 50% 0%,
      color-mix(in srgb, var(--nts-tint, #ff6a00) 22%, transparent) 0%,
      transparent 70%
    ),
    #000717;
}

// Os 11px abaixo do topo do painel do Figma, medidos DENTRO do cabeçalho: o
// painel começa a 75px do topo da arte, então o X cai em (75 + 11,17) / 218,318 da
// altura dela. Em % pra acompanhar a escala — e daí ele precisar do cabeçalho como
// régua, já que % em `top` resolve pela ALTURA do bloco contenedor.
.nts__close {
  @apply absolute z-[3];

  top: 39.47%;
  right: 14px;
  width: 30px;
  height: 30px;

  svg {
    @apply w-full h-full;
  }
}

// Partículas no chão do painel e o conteúdo um degrau acima: sem
// `position: relative` o texto não participa da ordem de pilha e uma camada em
// z-0 pinta por cima dele, por mais que ela venha antes no DOM.
.nts__particles {
  z-index: 0;
  // 30% mais discretas que no resto da casa: a folha de aviso é quase só texto
  // (não tem roda nem trilha competindo com o fundo), e no cheio as partículas
  // puxavam a leitura pra elas. A opacidade vai na CAMADA e não no componente:
  // o pico de cada ponto é sorteado lá dentro (0,35–0,9) e mexer nele mudaria o
  // fundo da roletinha e do check-in junto.
  opacity: 0.7;
}

.nts__message,
.nts__hint {
  @apply relative z-[1];
}

// Halo escuro atrás de TODO texto da folha: as partículas passam por baixo das
// letras (elas estão em z-0, o texto em z-1) e, quando um ponto brilhante cruza
// uma linha, o contraste local cai e a leitura tropeça. A sombra é da cor do
// próprio painel (#000717), então ela não se lê como sombra — só reforça o fundo
// no raio da letra: uma cópia curta e dura pra apoiar o traço, e uma larga e
// suave pra abafar o halo do ponto.
// Aqui não vale `text-shadow` numa regra só no `.nts__panel`: ela é herdada, e
// herdar uma sombra é o mesmo que declarar, então tanto faz — mas listar os
// alvos deixa explícito o que é texto de leitura e o que não é (o ✓ da lista é
// SVG e não recebe nada).
.nts__message,
.nts__hint,
.nts__reqs :deep(.lgn__text) {
  text-shadow:
    0 1px 2px rgba(0, 7, 23, 0.9),
    0 0 10px rgba(0, 7, 23, 0.75);
}

.nts__message {
  @apply text-white font-bold;

  font-size: 18px;
  line-height: 26px;
}

// Título do aviso de nível: centrado (o aviso abaixo dele já é centrado, e o
// título alinhado à esquerda ficava torto sobre ele) e com o trecho do nível na
// cor do elo — a MESMA do `<strong>` do corpo, pra ler como uma peça só.
.nts__message--gate {
  text-align: center;

  strong {
    color: var(--nts-accent, #ffc324);
  }
}

.nts__hint {
  @apply text-white/60;

  margin-top: 8px;
  font-size: 12px;
  line-height: 18px;
}

.nts__reqs {
  @apply relative z-[1];

  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

// Desktop: card centrado, como as outras folhas viram card no mesmo ponto.
@media (min-width: 1024px) {
  .nts__panel {
    padding-bottom: 33px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
  }
}
</style>
